import type { CSSProperties } from 'react';
import type { ComponentSpacing } from '@/config/types';

/**
 * `layout.components` -> the custom properties the `.widget-container` rule reads.
 *
 * The legacy app expressed this as Vue `v-bind()` inside `moduleLoader.vue`'s SCSS, which
 * compiles to exactly this: custom properties on an element, consumed by a static rule.
 *
 * Set **once, on the container** — not per widget. One write of three properties, inherited
 * by every `.widget-container` below it, instead of n identical inline style objects that
 * React has to diff on every render.
 *
 * Values are rem and default to 1 (applied by `componentSpacingSchema`, so nothing here
 * needs a fallback). The `.widget-container` rule still carries `1rem` defaults for the case
 * where a container forgets to set them at all.
 *
 * Shared with SYM-492's `SliderLayout`, which puts the same properties on the same class.
 */
export function componentSpacingVars(spacing: ComponentSpacing): CSSProperties {
  // The cast is the standard React escape hatch for custom properties: `CSSProperties` has
  // no index signature for them.
  return {
    '--widget-pt': `${String(spacing.topSpacing)}rem`,
    '--widget-pb': `${String(spacing.bottomSpacing)}rem`,
    '--widget-px': `${String(spacing.sideSpacing)}rem`,
  } as CSSProperties;
}
